// src/utils/metaPixel.js
/**
 * Utility functions for Meta (Facebook) Pixel event tracking.
 * Provides a safe wrapper around fbq() calls for React SPA usage.
 * 
 * Standard Events Documentation:
 * https://developers.facebook.com/docs/meta-pixel/reference
 */

/**
 * Safely tracks a Meta Pixel event.
 * Handles cases where the pixel hasn't loaded yet.
 * 
 * @param {string} eventName - Meta Pixel event name (e.g., 'Lead', 'Purchase', 'CompleteRegistration')
 * @param {Object} [parameters] - Optional event parameters
 * @returns {boolean} - True if event was tracked, false if fbq not available
 */
export function trackMetaPixelEvent(eventName, parameters = {}) {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      if (Object.keys(parameters).length > 0) {
        window.fbq('track', eventName, parameters);
      } else {
        window.fbq('track', eventName);
      }
      console.log(`[MetaPixel] Tracked event: ${eventName}`, parameters);
      return true;
    } else {
      console.warn(`[MetaPixel] fbq not available, could not track: ${eventName}`);
      return false;
    }
  } catch (error) {
    console.error(`[MetaPixel] Error tracking event ${eventName}:`, error);
    return false;
  }
}

/**
 * Tracks a Lead event in Meta Pixel.
 * Use when a user submits contact information indicating interest.
 * 
 * According to Meta documentation, Lead event should fire when:
 * - A user completes a signup indicating interest (e.g., form submission)
 * - User submits their contact info (email, phone, etc.)
 * 
 * @param {Object} [options] - Optional parameters for the Lead event
 * @param {string} [options.content_name] - Name of the content/form
 * @param {string} [options.content_category] - Category of the lead source
 * @param {number} [options.value] - Estimated value of the lead (optional)
 * @param {string} [options.currency] - Currency code if value is provided (e.g., 'USD')
 * @returns {boolean} - True if event was tracked successfully
 */
export function trackMetaLead(options = {}) {
  const parameters = {};
  
  if (options.content_name) {
    parameters.content_name = options.content_name;
  }
  if (options.content_category) {
    parameters.content_category = options.content_category;
  }
  if (options.value !== undefined && options.currency) {
    parameters.value = options.value;
    parameters.currency = options.currency;
  }
  
  return trackMetaPixelEvent('Lead', parameters);
}

export default {
  trackMetaPixelEvent,
  trackMetaLead,
};
